import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Radio, ChevronDown } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";

export default function Hero({ content }) {
  const images = content.hero_images || [];
  const lines = content.hero_title_lines || [];
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-navy">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <AnimatePresence mode="sync">
          {images.length > 0 && (
            <motion.img
              key={index}
              src={images[index]}
              alt="Research and academic imagery"
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6, ease: "easeInOut" }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
        </AnimatePresence>
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/60" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-32 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex items-center gap-3 text-[11px] sm:text-xs font-mono uppercase tracking-[0.18em] sm:tracking-[0.3em] text-sky mb-6 sm:mb-8"
          data-testid="hero-overline"
        >
          <span className="inline-block h-px w-10 bg-cherry" />
          {content.hero_overline}
        </motion.p>

        <h1 className="font-serif font-bold text-white tracking-tight leading-[1.08] text-[2rem] sm:text-6xl lg:text-7xl xl:text-8xl max-w-4xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === lines.length - 1 ? "italic" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === lines.length - 1 ? (
                  <>
                    {line.replace("research.", "")}
                    <span className="relative inline-block">
                      research.
                      <motion.span
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute -bottom-1 left-0 h-[3px] w-full bg-cherry origin-left"
                      />
                    </span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-7 max-w-xl text-base sm:text-lg font-sans text-white/70 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {content.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollToId("courses")}
            className="group flex items-center gap-3 bg-cherry hover:bg-[#B81919] text-white font-medium px-7 py-4 rounded-full transition-colors duration-300"
          >
            Explore Courses
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-workshop-button"
            onClick={() => content.workshop_form_url ? window.open(content.workshop_form_url, "_blank", "noopener") : scrollToId("live")}
            className="group flex items-center gap-3 border border-white/30 hover:border-white/60 backdrop-blur-sm text-white font-medium px-7 py-4 rounded-full transition-colors duration-300"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cherry opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cherry" />
            </span>
            Join Live Workshop
          </button>
        </motion.div>

        {images.length > 1 && (
          <div className="mt-14 flex items-center gap-2" data-testid="hero-slide-indicators">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1 rounded-full transition-all duration-500 ${i === index ? "w-10 bg-cherry" : "w-4 bg-white/30 hover:bg-white/50"}`}
              />
            ))}
          </div>
        )}
      </motion.div>

      <motion.button
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-8 z-10 hidden sm:flex flex-col items-center gap-2 text-white/50 hover:text-white transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
}
